import { Formik } from 'formik';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { FormModal, GFormFields } from '@components';
import { getOriginFormConfig, HEALTH_CHECK_FORM_SCHEMA } from '@constants';
import { Dictionaries, FormType, OriginEndpoint, originType } from '@enums';
import {
  useAppDispatch,
  useFetchFormList,
  useFormContent,
  useFormLoadingStatus,
  useFormSubmissionStatus,
} from '@hooks';
import { fetchFormList, submitFormRequest } from '@store';
import type { DialogBase, FormConfig, HealthCheckData } from '@types';
import { getOptionListFromCatalog, optionType, validateValueEqual } from '@utils';

export const HealthCheckConfigurationModal: FC<DialogBase> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();

  const formType = FormType.CdnOrigin;
  const endpoint = OriginEndpoint.HEALTH_CHECK;
  const schema = HEALTH_CHECK_FORM_SCHEMA;

  const currentForm = useFormContent({ formType });
  const healthCheckForm = useFormContent({ formType, endpoint });
  const healthCheckFormLoadingStatus = useFormLoadingStatus({ formType, endpoint });
  const healthCheckFormSubmissionStatus = useFormSubmissionStatus({ formType, endpoint });

  const isHttpsType = validateValueEqual(currentForm?.type, originType.HTTPS);

  const healthOptionList: optionType[] = useMemo(() => {
    const options = getOptionListFromCatalog({
      name: Dictionaries.ProtocolTcpType,
      hiddenOptions: [isHttpsType ? originType.HTTP : originType.HTTPS],
    });
    return options;
  }, []);

  useFetchFormList({
    formType,
    params: { content_id: currentForm?.id },
    globalOptions: {
      endpoint,
      content: true,
      endPath: true,
    },
  });

  const handleSubmit = (values: HealthCheckData) => {
    const { protocol, verify_ssl_cert, status, ...updatedValues } = values;

    const isHttpsProtocol = validateValueEqual(protocol, originType.HTTPS);
    const isTcpProtocol = validateValueEqual(protocol, 'tcp');

    const formData = {
      status,
      protocol,
      ...(!isTcpProtocol && { ...updatedValues }),
      ...(isHttpsProtocol && {
        verify_ssl_cert,
      }),
    };
    dispatch(
      submitFormRequest({
        formData,
        formType,
        params: { content_id: currentForm?.id },
        globalOptions: {
          endpoint,
          endPath: true,
        },
        callbackFunction: {
          refetch: fetchFormList({
            formType,
            params: { application_id },
            globalOptions: {
              pagination: true,
            },
          }),
        },
      }),
    );
  };

  const getProtocol = (currentType?: string, healthCheckProtocol?: string) => {
    const isTcpProtocol = validateValueEqual(healthCheckProtocol, 'tcp');
    if (isTcpProtocol) {
      return healthCheckProtocol ?? currentType ?? originType.HTTP;
    }

    return currentType;
  };

  const formConfig: FormConfig = useMemo(
    () =>
      getOriginFormConfig({
        endpoint,
        isLoading: healthCheckFormLoadingStatus,
        isDisabled: healthCheckFormSubmissionStatus,
        isHttpsType,
        schema,
        originOptionList: healthOptionList,
      }),
    [endpoint, healthCheckFormLoadingStatus, healthCheckFormSubmissionStatus, isHttpsType, schema, healthOptionList],
  );

  const initialValues: HealthCheckData = useMemo(() => {
    return {
      status: healthCheckForm?.status ?? false,
      host: healthCheckForm?.host ?? '',
      uri: healthCheckForm?.uri ?? '/',
      protocol: getProtocol(currentForm?.type, healthCheckForm?.protocol),
      verify_ssl_cert: healthCheckForm?.verify_ssl_cert ?? true,
    };
  }, [healthCheckForm]);

  const { inputList, label, validationSchema } = formConfig;

  if (!open || !inputList) return;

  return (
    <Formik<HealthCheckData>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ errors }) => {
        return (
          <FormModal
            formType={formType}
            title={label?.title}
            description={label?.description}
            onClose={onClose}
            size="lg"
            open={open}
            confirmButton={{ children: label?.confirmButton }}
          >
            <GFormFields inputList={inputList} errors={errors} />
          </FormModal>
        );
      }}
    </Formik>
  );
};
